import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, MailPlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { first_name: "", last_name: "", email: "", location_id: "" };

export const InviteTenantAdminDialog = ({ detail, onDone }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const locations = detail.locations || [];
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openDialog = () => {
    setForm({ ...EMPTY, location_id: locations[0]?.id || "" });
    setOpen(true);
  };

  const submit = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/tenants/${detail.id}/invite-admin`, form);
      toast.success(res.data.message);
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invite");
      if (err.response?.status === 502) { setOpen(false); onDone(); }
    } finally {
      setSending(false);
    }
  };

  const valid = /\S+@\S+\.\S+/.test(form.email) && form.location_id;
  return (
    <>
      <Button size="sm" variant="outline" onClick={openDialog} disabled={locations.length === 0} data-testid={`invite-tenant-admin-btn-${detail.id}`}>
        <MailPlus className="h-3.5 w-3.5 mr-1.5" /> Invite tenant admin
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="invite-tenant-admin-dialog">
          <DialogHeader>
            <DialogTitle>Invite tenant admin to {detail.name}</DialogTitle>
            <DialogDescription>
              They get an email with a one-time link to set their password (valid 7 days) and will see every location in this tenant.
              If the email already has an account, they're promoted and get a short heads-up instead.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>First name</Label><Input value={form.first_name} onChange={set("first_name")} data-testid="invite-first-name-input" /></div>
              <div><Label>Last name</Label><Input value={form.last_name} onChange={set("last_name")} data-testid="invite-last-name-input" /></div>
            </div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} placeholder="owner@example.com" data-testid="invite-email-input" /></div>
            <div>
              <Label>Home location</Label>
              <Select value={form.location_id} onValueChange={(v) => setForm((f) => ({ ...f, location_id: v }))}>
                <SelectTrigger data-testid="invite-location-select"><SelectValue placeholder="Pick a location" /></SelectTrigger>
                <SelectContent>
                  {locations.map((l) => <SelectItem key={l.id} value={l.id} data-testid={`invite-location-option-${l.id}`}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="invite-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={sending || !valid} className="bg-indigo-600 hover:bg-indigo-700" data-testid="invite-submit-btn">
              {sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Send invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
